import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { CurrencyStat, DashboardOverviewData, SettingItem, Currency, FilterGroup } from '../types';
import { DashboardOverview } from './DashboardOverview';
import { DashboardTimeView } from './DashboardTimeView';
import { CashIcon } from './icons/CashIcon';
import { ReceiptIcon } from './icons/ReceiptIcon';
import { logger } from '../services/logger';
import { apiService } from '../services/apiService';
import { Spinner } from './Spinner';
import { useReceiptsManager } from '../hooks/useReceiptsManager';
import { FilterIcon } from './icons/FilterIcon';
import { FilterPanel, FiltersState } from './FilterPanel';
import { CloseIcon } from './icons/CloseIcon';

interface DashboardProps {
  filters: ReturnType<typeof useReceiptsManager>['filters'];
  setFilters: ReturnType<typeof useReceiptsManager>['setFilters'];
  filterOptions: ReturnType<typeof useReceiptsManager>['filterOptions'];
}

export const Dashboard: React.FC<DashboardProps> = ({ filters, setFilters, filterOptions }) => {
    const { dateFrom, dateTo } = filters;
    const [activeTab, setActiveTab] = useState<'overview' | 'time'>('overview');
    const [selectedCurrency, setSelectedCurrency] = useState<string | null>(null);
    const [currencyStats, setCurrencyStats] = useState<CurrencyStat[]>([]);
    const [overviewData, setOverviewData] = useState<DashboardOverviewData | null>(null);
    const [isLoadingStats, setIsLoadingStats] = useState(true);
    const [isLoadingOverview, setIsLoadingOverview] = useState(false);
    const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);

    const activeFilterCount = useMemo(() => {
        let count = 0;
        count += filters.tags.include.length + filters.tags.exclude.length;
        count += filters.businessNames.include.length + filters.businessNames.exclude.length;
        count += filters.categories.include.length + filters.categories.exclude.length;
        count += filters.paymentMethods.include.length + filters.paymentMethods.exclude.length;
        count += filters.currencies.include.length + filters.currencies.exclude.length;
        return count;
    }, [filters]);
    
    const handleApplyFilters = (newFilters: FiltersState) => {
        setFilters(prev => ({
            ...prev,
            ...newFilters,
        }));
    };
    
    const handleClearAllFilters = () => {
        setFilters(prev => ({
            ...prev, // preserve dateFrom, dateTo
            tags: { include: [], exclude: [] },
            businessNames: { include: [], exclude: [] },
            categories: { include: [], exclude: [] },
            paymentMethods: { include: [], exclude: [] },
            currencies: { include: [], exclude: [] },
        }));
    };
    
    useEffect(() => {
        const fetchStats = async () => {
            setIsLoadingStats(true);
            try {
                const apiFilters = {
                    dateFrom: filters.dateFrom,
                    dateTo: filters.dateTo,
                    tags_include: filters.tags.include,
                    tags_exclude: filters.tags.exclude,
                    businessNames_include: filters.businessNames.include,
                    businessNames_exclude: filters.businessNames.exclude,
                    categories_include: filters.categories.include,
                    categories_exclude: filters.categories.exclude,
                    paymentMethods_include: filters.paymentMethods.include,
                    paymentMethods_exclude: filters.paymentMethods.exclude,
                    currencies_include: filters.currencies.include,
                    currencies_exclude: filters.currencies.exclude,
                };
                const stats = await apiService.getDashboardStats(apiFilters);
                setCurrencyStats(stats);
                
                if (stats.length > 0) {
                    setSelectedCurrency(current => {
                        const selectionIsValid = stats.some(s => s.name === current);
                        return (!current || !selectionIsValid) ? stats[0].name : current;
                    });
                } else {
                    setSelectedCurrency(null);
                }
            } catch (error) {
                logger.error("Failed to fetch dashboard stats:", error);
                setCurrencyStats([]);
                setSelectedCurrency(null);
            } finally {
                setIsLoadingStats(false);
            }
        };
        fetchStats();
    }, [filters]);

    useEffect(() => {
        if (!selectedCurrency) {
            setOverviewData(null);
            return;
        };

        const fetchOverview = async () => {
            setIsLoadingOverview(true);
            try {
                 const apiFilters = {
                    dateFrom: filters.dateFrom,
                    dateTo: filters.dateTo,
                    tags_include: filters.tags.include,
                    tags_exclude: filters.tags.exclude,
                    businessNames_include: filters.businessNames.include,
                    businessNames_exclude: filters.businessNames.exclude,
                    categories_include: filters.categories.include,
                    categories_exclude: filters.categories.exclude,
                    paymentMethods_include: filters.paymentMethods.include,
                    paymentMethods_exclude: filters.paymentMethods.exclude,
                };
                const data = await apiService.getDashboardOverview(apiFilters, selectedCurrency);
                setOverviewData(data);
            } catch (error) {
                logger.error(`Failed to fetch dashboard overview for ${selectedCurrency}:`, error);
                setOverviewData(null);
            } finally {
                setIsLoadingOverview(false);
            }
        };

        fetchOverview();
    }, [filters, selectedCurrency]);


    if (isLoadingStats) {
        return <div className="flex justify-center py-10"><Spinner text="Načítám statistiky..." /></div>;
    }

    if (currencyStats.length === 0) {
        return <div className="text-center text-gray-500 py-10">Žádná data k zobrazení v dashboardu pro vybrané období.</div>;
    }
    
    const TabButton: React.FC<{ tabId: 'overview' | 'time'; label: string }> = ({ tabId, label }) => (
        <button
            onClick={() => setActiveTab(tabId)}
            className={`px-4 py-2 text-sm font-semibold rounded-md transition-colors ${
                activeTab === tabId
                    ? 'bg-blue-600 text-white shadow'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-100'
            }`}
        >
            {label}
        </button>
    );

    return (
        <div className="space-y-8">
            <div className="mb-4">
                <div className="flex items-center gap-4">
                    <button onClick={() => setIsFilterPanelOpen(true)} className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-900 font-medium">
                        <FilterIcon className="w-4 h-4" />
                        Filtrovat
                        {activeFilterCount > 0 && (
                            <span className="ml-1 bg-blue-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">{activeFilterCount}</span>
                        )}
                    </button>
                    {activeFilterCount > 0 && (
                        <button onClick={handleClearAllFilters} className="flex items-center gap-1 text-sm text-red-500 hover:text-red-700 font-medium" title="Vymazat všechny filtry">
                            <CloseIcon className="w-4 h-4" />
                            <span>Vymazat</span>
                        </button>
                    )}
                </div>
            </div>
             <FilterPanel
                isOpen={isFilterPanelOpen}
                onClose={() => setIsFilterPanelOpen(false)}
                onApply={handleApplyFilters}
                initialFilters={{
                    tags: filters.tags,
                    businessNames: filters.businessNames,
                    categories: filters.categories,
                    paymentMethods: filters.paymentMethods,
                    currencies: filters.currencies,
                }}
                filterOptions={filterOptions}
            />
            
            <div>
                 <h3 className="text-lg font-semibold text-gray-700 mb-4">Přehled dle měny (aplikuje se na záložku Přehled výdajů)</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {currencyStats.map(stat => (
                        <button
                            key={stat.name}
                            onClick={() => setSelectedCurrency(stat.name)}
                            className={`p-4 rounded-lg text-left transition-all duration-200 ${
                                selectedCurrency === stat.name
                                    ? 'bg-blue-600 text-white shadow-lg ring-2 ring-offset-2 ring-blue-600'
                                    : 'bg-white text-gray-800 shadow hover:shadow-md hover:-translate-y-1'
                            }`}
                        >
                            <h4 className="font-bold text-2xl">{stat.name}</h4>
                            <div className="mt-4 space-y-2">
                                 <div className="flex items-center gap-2">
                                    <CashIcon className="w-5 h-5 opacity-70" />
                                    <span className="text-lg font-semibold">{stat.totalAmount.toLocaleString('cs-CZ')}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <ReceiptIcon className="w-5 h-5 opacity-70" />
                                    <span className="text-sm">{stat.receiptCount} {stat.receiptCount === 1 ? 'položka' : stat.receiptCount > 1 && stat.receiptCount < 5 ? 'položky' : 'položek'}</span>
                                </div>
                            </div>
                        </button>
                    ))}
                </div>
            </div>

            <div className="flex justify-center items-center gap-2 flex-wrap border-t pt-6" role="group" aria-label="Navigace v dashboardu">
                <TabButton tabId="overview" label="Přehled výdajů" />
                <TabButton tabId="time" label="Výdaje v čase" />
            </div>

            <div className="mt-6">
                {activeTab === 'overview' && (
                    isLoadingOverview ? <div className="flex justify-center py-10"><Spinner text="Načítám přehled..." /></div> :
                    <DashboardOverview 
                        expenseData={overviewData?.expenseData ?? []}
                        refundData={overviewData?.refundData ?? []}
                        selectedCurrency={selectedCurrency}
                    />
                )}
                {activeTab === 'time' && (
                    <DashboardTimeView
                        selectedCurrency={selectedCurrency}
                        dateFrom={dateFrom}
                        dateTo={dateTo}
                    />
                )}
            </div>
        </div>
    );
};